import { FC, ReactNode, useState } from 'react';
import { Typography } from '../../components/Typography';
import { Goal, Assist, PenaltyMiss, PenaltySave, ChevronUp } from '../../components/svgs';
import {
  TopTab,
  RowIcon,
  ScoringRow,
  ScoringContent,
  ParagraphContent,
  ContentData,
  DEFAULT_CONTENT,
  GuideSection,
  GuideRow,
} from './types';

const RowIconFrame: FC<{ children: ReactNode; label?: string }> = ({ children, label }) => (
  <span
    className="inline-flex size-6 shrink-0 items-center justify-center overflow-visible"
    aria-label={label}
  >
    {children}
  </span>
);

const RowIconComponent: FC<{ icon: RowIcon }> = ({ icon }) => {
  const cls = 'size-5 overflow-visible';

  switch (icon) {
    case 'goal':
      return (
        <RowIconFrame>
          <Goal className={cls} />
        </RowIconFrame>
      );
    case 'own-goal':
      return (
        <RowIconFrame>
          <Goal className={`${cls} opacity-60`} />
        </RowIconFrame>
      );
    case 'assist':
      return (
        <RowIconFrame>
          <Assist className={cls} />
        </RowIconFrame>
      );
    case 'penalty-miss':
      return (
        <RowIconFrame>
          <PenaltyMiss className={cls} />
        </RowIconFrame>
      );
    case 'penalty-save':
      return (
        <RowIconFrame>
          <PenaltySave className={cls} />
        </RowIconFrame>
      );
    case 'yellow-card':
      return (
        <RowIconFrame label="Yellow card">
          <span className="inline-block h-5 w-[14px] rounded-[1.4px] border border-white bg-[#ffcb00] shadow-[0_0_2px_0_rgba(0,0,0,0.2)]" />
        </RowIconFrame>
      );
    case 'red-card':
      return (
        <RowIconFrame label="Red card">
          <span className="inline-block h-5 w-[14px] rounded-[1.4px] border border-white bg-[#eb1701] shadow-[0_0_2px_0_rgba(0,0,0,0.1)]" />
        </RowIconFrame>
      );
    default:
      return null;
  }
};

const PositionBadge: FC<{ label: string }> = ({ label }) => (
  <span className="inline-flex min-w-12 shrink-0 items-center justify-center rounded-[--corner-xs] bg-[#eae9fd] px-[--padding-xs] py-[--padding-3xs]">
    <Typography
      as="span"
      variant="label010"
      className="text-center text-[#222526] whitespace-nowrap text-[14px]"
    >
      {label}
    </Typography>
  </span>
);

const ScoringRowItem: FC<{ row: ScoringRow; isLast: boolean }> = ({ row, isLast }) => (
  <div
    className={`flex w-full items-start gap-[--padding-md] py-[--padding-sm] ${isLast ? '' : 'border-b border-[#eae9fd]'
      }`}
  >
    <div className="flex min-w-0 flex-1 items-start gap-[--padding-xs]">
      {row.badge && <PositionBadge label={row.badge} />}

      <div className="flex min-w-0 flex-1 items-start gap-[--padding-xs]">
        <Typography
          as="span"
          variant="subheadline010"
          className="min-w-0 break-words text-[#222526] text-[14px]"
        >
          {row.label}
          {row.note && (
            <Typography as="span" variant="paragraph010" className="text-[#222526] text-[14px]">
              {row.note}
            </Typography>
          )}
          {row.icon && (
            <span className="inline-flex align-middle ml-[--padding-xs]">
              <RowIconComponent icon={row.icon} />
            </span>
          )}
        </Typography>
      </div>
    </div>

    <Typography
      as="span"
      variant="button020"
      className="max-w-[96px] shrink-0 text-right text-[#222526] text-[14px]"
    >
      {row.points}
    </Typography>
  </div>
);

const LearnMoreButton: FC<{ fullWidth?: boolean }> = ({ fullWidth }) => (
  <button
    type="button"
    className={`flex h-12 items-center justify-center gap-2 rounded-[4px] bg-[#0072ee] px-6 ${
      fullWidth ? 'w-full' : 'shrink-0'
      }`}
  >
    <Typography as="span" variant="button020" className="text-white whitespace-nowrap">
      Learn More
    </Typography>
  </button>
);

const PpmFooter: FC = () => (
  <div className="flex w-full items-center justify-between gap-[--padding-md]">
    <div className="flex flex-col gap-[--padding-xs]">
      <span className="inline-flex w-12 items-center justify-center rounded-[--corner-xs] bg-[#201d4a] px-[--padding-xs] py-[--padding-3xs]">
        <Typography as="span" variant="label010" className="text-center text-white">
          PPM
        </Typography>
      </span>
      <Typography as="p" variant="paragraph010" className="text-[#222526]">
        Player Performance Metrics
      </Typography>
    </div>

    <LearnMoreButton />
  </div>
);

const GuideRows: FC<{ rows: GuideRow[]; variant?: 'card' | 'dropdown' }> = ({
  rows,
  variant = 'card',
}) => {
  if (variant === 'dropdown') {
    return (
      <div className="flex flex-col pl-2">
        {rows.map((row, index) => (
          <div
            key={`${row.label}-${index}`}
            className={`flex flex-col gap-1 px-3 py-3 bg-[#EAE9FD] ${index === rows.length - 1 ? '' : 'border-b border-white'
              }`}
          >
            <div className="flex items-start justify-between gap-1">
              <Typography as="span" variant="subheadline010" className="text-[#222526] text-[14px]">
                {row.label}
              </Typography>
              {row.value && (
                <Typography
                  as="span"
                  variant="paragraph010"
                  className="shrink-0 text-right text-[#222526] text-[14px]"
                >
                  {row.value}
                </Typography>
              )}
            </div>

            {row.description && (
              <Typography as="p" variant="paragraph010" className="text-[#222526] text-[14px]">
                {row.description}
              </Typography>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[--corner-xs] border border-[#eae9fd] bg-white">
      {rows.map((row, index) => (
        <div
          key={`${row.label}-${index}`}
          className={`flex flex-col gap-1 px-3 py-3 ${index === rows.length - 1 ? '' : 'border-b border-[#eae9fd]'
            }`}
        >
          <div className="flex items-start justify-between gap-1">
            <Typography as="span" variant="subheadline010" className="text-[#222526] text-[14px]">
              {row.label}
            </Typography>
            {row.value && (
              <Typography
                as="span"
                variant="paragraph010"
                className="shrink-0 text-right text-[#222526] text-[14px]"
              >
                {row.value}
              </Typography>
            )}
          </div>

          {row.description && (
            <Typography as="p" variant="paragraph010" className="text-[#222526] text-[14px]">
              {row.description}
            </Typography>
          )}
        </div>
      ))}
    </div>
  );
};

const GuideNote: FC<{ children: string }> = ({ children }) => (
  <Typography
    as="p"
    variant="paragraph010"
    className="rounded-[--corner-xs] bg-[#f8f8ff] px-3 py-2 text-[#222526] text-[14px]"
  >
    {children}
  </Typography>
);

const GuideSectionPanel: FC<{ section: GuideSection; dropdownMode?: boolean }> = ({
  section,
  dropdownMode = false,
}) => {
  const [isOpen, setIsOpen] = useState(!section.collapsible);
  const isDropdown = dropdownMode || section.collapsible;

  const content = (
    <>
      {section.paragraphs?.map((text, index) => (
        <Typography
          key={`${text}-${index}`}
          as="p"
          variant="paragraph010"
          className="text-[#222526] text-[14px] text-[14px] pl-2"
        >
          {text}
        </Typography>
      ))}

      {section.rows && <GuideRows rows={section.rows} variant={isDropdown ? 'dropdown' : 'card'} />}

      {section.items && (
        <ul className="list-disc space-y-1">
          {section.items.map((item, index) => (
            <Typography
              key={`${item.label}-${index}`}
              as="li"
              variant="paragraph010"
              className="text-[#222526] text-[14px]"
            >
              {item.value ? `${item.label} ${item.value}` : item.label}
            </Typography>
          ))}
        </ul>
      )}

      {section.note && <GuideNote>{section.note}</GuideNote>}

      {section.sections && <GuideSections sections={section.sections} dropdownMode={isDropdown} applyPadding={false} />}
    </>
  );

  return (
    <section className="flex flex-col gap-[--padding-xs]">
      {section.title &&
        (section.collapsible ? (
          <button
            type="button"
            onClick={() => setIsOpen(prev => !prev)}
            className="flex items-center justify-between w-full text-left"
          >
            <Typography as="h3" variant="subheadline020" className="text-[#222526]">
              {section.title}
            </Typography>
            <ChevronUp
              className={`size-5 shrink-0 text-[#222526] transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`}
            />
          </button>
        ) : (
          <Typography as="h3" variant="subheadline020" className="text-[#222526]">
            {section.title}
          </Typography>
        ))}

      {(!section.collapsible || isOpen) &&
        (isDropdown ? (
          <div className="bg-[#EAE9FD]  rounded-[--corner-xs] flex flex-col gap-[--padding-xs]">
            {content}
          </div>
        ) : (
          content
        ))}
    </section>
  );
};

const GuideSections: FC<{ sections?: GuideSection[]; dropdownMode?: boolean, applyPadding?: boolean, applyBg?: boolean }> = ({
  sections,
  dropdownMode = false,
  applyPadding = true,
  applyBg = false
}) => {
  if (!sections?.length) {
    return null;
  }

  return (
    <div className={`flex flex-col gap-[--padding-md] ${applyBg ? 'bg-[#EAE9FD]' : ''} ${applyPadding ? 'rounded-[4px] px-4 py-3' : ''}`}>
      {sections.map((section, index) => (
        <GuideSectionPanel
          key={`${section.title ?? 'section'}-${index}`}
          section={section}
          dropdownMode={dropdownMode}
        />
      ))}
    </div>
  );
};

const ScoringPanel: FC<{ data: ScoringContent }> = ({ data }) => (
  <div className="flex w-full flex-col gap-[--padding-md]">
    {data.paragraphs?.map((text, index) => (
      <Typography
        key={`${text}-${index}`}
        as="p"
        variant="paragraph010"
        className="text-[#222526] [&_a]:text-[#0072ee] [&_a]:underline hover:[&_a]:text-[#0056b3] text-[14px]"
        dangerouslySetInnerHTML={{ __html: text }}
      />
    ))}

    <div className="flex w-full flex-col">
      {data.rows.map((row, index) => (
        <ScoringRowItem
          key={`${row.label}-${index}`}
          row={row}
          isLast={index === data.rows.length - 1 && !data.showLearnMore && !data.ppmFooter}
        />
      ))}

      {data.showLearnMore && (
        <div className="pt-[--padding-xs]">
          <LearnMoreButton fullWidth />
        </div>
      )}

      {data.ppmFooter && (
        <div className="pt-[--padding-xs]">
          <PpmFooter />
        </div>
      )}
    </div>

    {data.note && <GuideNote>{data.note}</GuideNote>}
    <GuideSections sections={data.sections} applyBg={true} />
  </div>
);

const ParagraphPanel: FC<{ data: ParagraphContent }> = ({ data }) => (
  <div className="flex w-full flex-col gap-[--padding-md]">
    {data.paragraphs?.map((text, index) => (
      <Typography
        key={`${text}-${index}`}
        as="p"
        variant="paragraph010"
        className="text-[#222526] [&_a]:text-[#0072ee] [&_a]:underline hover:[&_a]:text-[#0056b3] text-[14px]"
        dangerouslySetInnerHTML={{ __html: text }}
      />
    ))}

    <GuideSections sections={data.sections} />
  </div>
);

export interface QuickGuideContentProps {
  topTab: TopTab;
  subTab: string;
  contentOverride?: Record<TopTab, Record<string, ContentData>>;
}

export const QuickGuideContent: FC<QuickGuideContentProps> = ({
  topTab,
  subTab,
  contentOverride,
}) => {
  const contentMap = contentOverride ?? DEFAULT_CONTENT;
  const data = contentMap[topTab]?.[subTab];

  if (!data) {
    return (
      <Typography as="p" variant="paragraph010" className="text-[#222526]">
        No content available.
      </Typography>
    );
  }

  return data.kind === 'scoring' ? <ScoringPanel data={data} /> : <ParagraphPanel data={data} />;
};
